import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Star } from "lucide-react";
import { motion } from "framer-motion";

export const Testimonials = () => {
  const testimonials = [
    {
      quote:
        "Rently transformed my rental property from a constant headache to a profitable investment. They significantly increased my rent and found an amazing long-term tenant.",
      name: "Sarah Johnson",
      role: "Property Owner, Overland Park",
      property: "Single Family Home in Overland Park",
      rating: 5,
    },
    {
      quote:
        "As someone with multiple properties across Johnson County, I needed a management company I could trust. Rently's dashboard gives me real-time insights and their maintenance team is phenomenal.",
      name: "Michael Chen",
      role: "Real Estate Investor, Leawood",
      property: "Multi-Property Portfolio",
      rating: 5,
    },
    {
      quote:
        "The rental process was so smooth! Online application, digital lease signing, and whenever I have maintenance issues, they're resolved within 24 hours. Best rental experience I've ever had.",
      name: "Jennifer Martinez",
      role: "Tenant, Shawnee",
      property: "Townhome in Shawnee, KS",
      rating: 5,
    },
  ];

  return (
    <section id="testimonials" className="py-14 sm:py-18 lg:py-22 bg-secondary/20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Real Owners. Real Results. Real Reviews.
          </h2>
          <p className="text-lg md:text-xl text-foreground/80 max-w-3xl mx-auto font-medium">
            See why property owners and tenants across Johnson County choose Rently Properties
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <Carousel
            data-testid="testimonials-carousel"
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent>
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/2">
                  <Card
                    data-testid="testimonials-item"
                    className="h-full shadow-[0_10px_30px_rgba(15,42,74,0.06)]"
                  >
                    <CardContent className="p-6">
                      <div className="flex mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star
                            key={i}
                            className="h-4 w-4 fill-accent text-accent"
                          />
                        ))}
                      </div>
                      <blockquote className="text-sm sm:text-base leading-relaxed text-foreground/90 mb-6">
                        "{testimonial.quote}"
                      </blockquote>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback className="bg-accent/10 text-accent font-semibold">
                            {testimonial.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-semibold text-sm text-foreground">
                            {testimonial.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {testimonial.role}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {testimonial.property}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-left-4 sm:-left-12" />
            <CarouselNext className="-right-4 sm:-right-12" />
          </Carousel>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
        >
          {[
            { value: "$1,548", label: "Avg Market Rent (OP)" },
            { value: "High", label: "Collection Rate" },
            { value: "4.9★", label: "Average Rating" },
            { value: "Proven", label: "Track Record" },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-2xl sm:text-3xl font-semibold font-display tabular-nums text-foreground">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground mt-1">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
